import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { AxiosResponse } from 'axios'
import { v4 as uuidv4 } from 'uuid'
import { NoFollowUpReasonEnum } from '../../containers/PostAppointment/Orders'
import useAppointmentNotes, {
  CreateAddendumResponse,
} from '../../hooks/useAppointmentNotes'
import useMember from '../../hooks/useMember'
import { Appointment, AppointmentNote } from '../../types/types'
import {
  AddendumOpInput,
  AmendValues,
  buildAddendumOps,
  noteToAmendValues,
} from '../../utils/appointment-note-addendum.utils'
import {
  nextScreen as getNextScreen,
  previousScreen as getPreviousScreen,
  editNextScreen as getEditNextScreen,
  editPreviousScreen as getEditPreviousScreen,
  editScreens,
  listScreens,
} from '../../utils/post-appointment.utils'
import { splitReferToSpecialist } from '../../utils/refer-to-specialist.utils'
import { useAlertContext } from '../AlertContext'
import { useSourcePluginContext } from '../SourcePluginContext'

interface PostAppointmentInterface {
  currentScreen: PostAppointmentScreen
  noFollowUpReason?: string
  labsResponse?: boolean
  imagingResponse?: boolean
  referToSpecialist?: string
  rxInterest?: boolean
  rxInterestMedications?: string[]
  timeline: string
  timelineDate: string
  noteForPatient: string
  recommendedProgram?: string
  stepper: PostAppointmentScreenStepper[]
  selectedFiles: File[][]
  selectedNote: AppointmentNote | undefined
  selectedAppointment: Appointment | undefined
  isLoading: boolean
  showNoFollowUp: boolean
  noFollowUpOtherReason: string
  isEditingNoteForPatient: boolean
  isSavingNoteForPatient: boolean
  originalNoteForPatient: string | null
  hasNoteForPatientChanged: boolean
  setCurrentScreen: (screen: PostAppointmentScreen) => void
  updateTestsResponse: (
    labsOrdered: boolean,
    imagingOrdered: boolean,
    referToSpecialist: string,
    rxInterest: boolean,
    rxInterestMedications: string[]
  ) => void
  updateTimeline: (value: string, isDate: boolean) => void
  nextScreen: (appointment?: Appointment, context?: NextScreenContext) => void
  previousScreen: () => void
  updateNoReasonFollowUp: (value: string) => void
  updateEditNoFollowup: (reason: string) => void
  updateNoteForPatient: (event: React.ChangeEvent<{ value: string }>) => void
  updateRecommendedProgram: (
    event: React.ChangeEvent<{ value: string }>
  ) => void
  handleAttachments: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleRemoveAttachment: (index: number) => void
  updateSelectedNote: (note: AppointmentNote | undefined) => void
  updateSelectedAppointment: (appointment: Appointment | undefined) => void
  confirmAndSend: (updateNoteId?: string, saveAsDraft?: boolean) => void
  updateShowFollowUp: () => void
  updateNoFollowUpOtherReason: (
    event: React.ChangeEvent<{ value: string }>
  ) => void
  startEditingNoteForPatient: () => void
  cancelEditingNoteForPatient: () => void
  saveNoteForPatient: () => Promise<void>
  handleCreateAddendum: (dto: {
    noteId: string
    ops: AddendumOpInput[]
    files?: File[][]
    comment?: string
  }) => Promise<AxiosResponse<CreateAddendumResponse>>
  removedAttachmentIds: string[]
  toggleAttachmentRemoval: (id: string) => void
  isEditing: boolean
  editOps: AddendumOpInput[]
  editValues: AmendValues
  editBaseline?: AmendValues
  editSavedMessage: string
  startEdit: (note: AppointmentNote, appointment: Appointment) => void
  cancelEdit: () => void
  saveEdit: (reason: string) => Promise<void>
  resetValues: () => void
}

/** The date input is ISO; the note stores the same MM/DD/YYYY the wizard sends. */
const formatFollowupDate = (isoDate?: string): string => {
  if (!isoDate) {
    return ''
  }

  const [year, month, day] = isoDate.split('-')
  return `${month}/${day}/${year}`
}

export const NOTE_UPDATED_MESSAGE =
  'Note updated. No message was sent to the member. Edit logged below.'

export const PostAppointmentContext = createContext<PostAppointmentInterface>(
  {} as PostAppointmentInterface
)

interface PostAppointmentProps {
  children: React.ReactNode
}

/** 'amend' is reached from Review, not from the wizard, so it has no stepper entry. */
export type PostAppointmentScreen =
  | 'initial'
  | 'recommended-program'
  | 'orders'
  | 'timeline'
  | 'note'
  | 'review'
  | 'amend-review'

export type PostAppointmentScreenStepper = {
  title: string
  index: number
  screen: PostAppointmentScreen
}

export type NextScreenContext = {
  moreTestsOrdered?: boolean
  noFollowUp?: boolean
}

export const PostAppointmentProvider: React.FC<PostAppointmentProps> = ({
  children,
}) => {
  const { setAlertText } = useAlertContext()
  const { pluginContext } = useSourcePluginContext()
  const { member } = useMember(pluginContext?.member ?? undefined)
  const { handleSaveNote, handleSaveNoteForPatient, handleCreateAddendum } =
    useAppointmentNotes()
  const [labsResponse, setLabsResponse] = useState<boolean | undefined>(
    undefined
  )
  const [imagingResponse, setImagingResponse] = useState<boolean | undefined>(
    undefined
  )
  const [referToSpecialist, setReferToSpecialist] = useState<
    string | undefined
  >(undefined)
  const [rxInterest, setRxInterest] = useState<boolean | undefined>(undefined)
  const [rxInterestMedications, setRxInterestMedications] = useState<string[]>(
    []
  )
  const [currentScreen, setCurrentScreen] =
    useState<PostAppointmentScreen>('initial')
  const [noFollowUpReason, setNoFollowUpReason] = useState<string | undefined>(
    undefined
  )
  const [timeline, setTimeline] = useState<string>('')
  const [timelineDate, setTimelineDate] = useState<string>('')
  const [noteForPatient, setNoteForPatient] = useState<string>('')
  const [recommendedProgram, setRecommendedProgram] = useState<
    string | undefined
  >(undefined)
  const [selectedFiles, setSelectedFiles] = useState<File[][]>([])
  const [selectedNote, setSelectedNote] = useState<AppointmentNote | undefined>(
    undefined
  )
  const [selectedAppointment, setSelectedAppointment] = useState<
    Appointment | undefined
  >(undefined)
  const [stepper, setStepper] = useState<PostAppointmentScreenStepper[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showNoFollowUp, setShowNoFollowUp] = useState<boolean>(false)
  const [noFollowUpOtherReason, setNoFollowUpOtherReason] = useState<string>('')
  const [isEditingNoteForPatient, setIsEditingNoteForPatient] = useState(false)
  const [isSavingNoteForPatient, setIsSavingNoteForPatient] = useState(false)
  const [originalNoteForPatient, setOriginalNoteForPatient] = useState<
    string | null
  >(null)
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([])
  const toggleAttachmentRemoval = useCallback((id: string) => {
    setRemovedAttachmentIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    )
  }, [])
  const [isEditing, setIsEditing] = useState(false)
  const [editBaseline, setEditBaseline] = useState<AmendValues | undefined>(
    undefined
  )
  const [editSavedMessage, setEditSavedMessage] = useState('')

  /**
   * Determines if the current "note for patient" text differs from the original signed note.
   * Only relevant for signed notes - always returns false for draft/unsaved notes.
   */
  const hasNoteForPatientChanged = useMemo(() => {
    if (!selectedNote || selectedNote.status !== 'signed') {
      return false
    }

    return noteForPatient !== selectedNote.text
  }, [noteForPatient, selectedNote])

  const updateTestsResponse = (
    labsOrdered: boolean,
    imagingOrdered: boolean,
    referToSpecialist: string,
    rxInterestValue: boolean,
    rxInterestMedicationsValue: string[]
  ) => {
    const testsOrdered = labsOrdered || imagingOrdered
    setLabsResponse(labsOrdered)
    setImagingResponse(imagingOrdered)
    setReferToSpecialist(referToSpecialist ?? undefined)
    setRxInterest(rxInterestValue)
    setRxInterestMedications(rxInterestMedicationsValue)
    nextScreen(undefined, { moreTestsOrdered: testsOrdered })
  }

  const updateNoReasonFollowUp = (value: string) => {
    setNoFollowUpReason(value)
  }

  /**
   * Withdraw the follow-up during an edit. Unlike updateShowFollowUp this
   * touches nothing but the follow-up, because an edit that quietly cleared the
   * note's orders would write ops the provider never asked for.
   */
  const updateEditNoFollowup = useCallback((reason: string) => {
    setNoFollowUpReason(reason || undefined)
    setNoFollowUpOtherReason('')
    setTimeline('')
    setTimelineDate('')
  }, [])

  const updateTimeline = (value: string, isDate: boolean) => {
    // The two follow-up controls were only one-way exclusive: choosing an
    // interval left the withdrawal reason showing beneath it.
    if (isEditing && value) {
      setNoFollowUpReason(undefined)
      setNoFollowUpOtherReason('')
    }

    if (isDate) {
      setTimelineDate(value)
      setTimeline('')
    } else {
      setTimeline((prev) => (prev === value ? '' : value))
      setTimelineDate('')
    }
  }

  const updateRecommendedProgram = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    setRecommendedProgram(event.target.value)
  }

  const handleAttachments = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files || undefined

    if (files) {
      setSelectedFiles([...selectedFiles, Array.from(files)])
    }
  }

  const handleRemoveAttachment = (index: number) => {
    setSelectedFiles([
      selectedFiles.flat().filter((_, fileIndex) => fileIndex !== index),
    ])
  }

  /**
   * Updates the selected appointment note and synchronizes related states.
   * When a note is selected, its "note for patient" text is copied to the noteForPatient state.
   * For signed notes, this enables inline editing of the "note for patient" field.
   * For unsaved/draft notes, the text can only be modified through the regular note creation flow.
   *
   * @param note - The appointment note to select, or undefined to reset all states
   */
  const updateSelectedNote = (note: AppointmentNote | undefined) => {
    if (note) {
      setNoteForPatient(note.text)

      if (typeof note.labsOrdered === 'boolean') {
        setLabsResponse(note.labsOrdered)
      }

      if (typeof note.imagingOrdered === 'boolean') {
        setImagingResponse(note.imagingOrdered)
      }

      setRxInterest(note.rxInterest ?? false)
      setRxInterestMedications(note.rxInterestMedications ?? [])

      if (note.recommendedProgram) {
        setRecommendedProgram(note.recommendedProgram)
      }

      if (note.followup) {
        setTimeline(note.followup)
      }

      if (note.noFollowupReason) {
        setNoFollowUpReason(note.noFollowupReason)
        setNoFollowUpOtherReason(note.noFollowupReason)
      }
    } else {
      reset()
    }

    setSelectedNote(note)
  }

  const updateSelectedAppointment = (appointment: Appointment | undefined) => {
    setSelectedAppointment(appointment)

    if (appointment) {
      setStepper(listScreens(appointment.type))
    }
  }

  const updateShowFollowUp = useCallback(() => {
    setShowNoFollowUp(!showNoFollowUp)
    setNoFollowUpOtherReason('')
    setNoFollowUpReason('')
    setLabsResponse(undefined)
    setImagingResponse(undefined)
    setReferToSpecialist(undefined)
    setRxInterest(undefined)
    setRxInterestMedications([])
    setTimeline('')
    setTimelineDate('')
  }, [showNoFollowUp])

  const updateNoFollowUpOtherReason = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    setNoFollowUpOtherReason(event.target.value)
  }

  const confirmAndSend = async (updateNoteId?: string, saveAsDraft = false) => {
    if (!member?.sourceHealthId) {
      setAlertText('Unable to save note, no member loaded in context')
      return
    } else if (!selectedAppointment) {
      setAlertText('Unable to save, no selected appointment')
      return
    }

    setIsLoading(true)

    const finalTimeline = showNoFollowUp
      ? undefined
      : timeline || formatFollowupDate(timelineDate)
    const finalNoFollowUpReason =
      noFollowUpReason === NoFollowUpReasonEnum.OTHER
        ? noFollowUpOtherReason
        : noFollowUpReason

    return handleSaveNote({
      note: {
        id: updateNoteId ?? uuidv4(),
        text: noteForPatient,
        status: saveAsDraft ? 'draft' : 'signed',
        timestamp: new Date().toISOString(),
        user: member,
        labsOrdered: !!labsResponse,
        imagingOrdered: !!imagingResponse,
        referToSpecialist: referToSpecialist || undefined,
        rxInterest: !!rxInterest,
        rxInterestMedications: rxInterest ? rxInterestMedications : [],
        recommendedProgram: recommendedProgram,
        appointment: selectedAppointment,
        followup: finalTimeline,
        noFollowupReason: finalNoFollowUpReason,
      },
      files: selectedFiles,
    }).then(() => {
      nextScreen()
      setIsLoading(false)
    })
  }

  const nextScreen = useCallback(
    (appointment?: Appointment, context?: NextScreenContext) => {
      let appointmentType = undefined

      if (selectedAppointment) {
        appointmentType = selectedAppointment.type
      } else if (appointment) {
        appointmentType = appointment.type
      } else {
        return
      }

      const screen = isEditing
        ? getEditNextScreen(currentScreen, appointmentType)
        : getNextScreen(currentScreen, appointmentType, context)
      setCurrentScreen(screen)
    },
    [
      currentScreen,
      selectedAppointment,
      showNoFollowUp,
      isEditing,
      setCurrentScreen,
    ]
  )

  const reset = () => {
    setLabsResponse(undefined)
    setImagingResponse(undefined)
    setReferToSpecialist(undefined)
    setRxInterest(undefined)
    setRxInterestMedications([])
    setSelectedFiles([])
    setStepper([])
    setNoFollowUpOtherReason('')
    setShowNoFollowUp(false)
    setNoteForPatient('')
    setTimeline('')
    setRecommendedProgram(undefined)
    setNoFollowUpReason(undefined)
    setSelectedAppointment(undefined)
    setSelectedNote(undefined)
    setEditSavedMessage('')
  }

  /**
   * Same composition confirmAndSend uses, so an edit and a sign agree. Not gated
   * on showNoFollowUp: an edit records the withdrawal on the follow-up step,
   * which never wipes the note's orders the way the Orders path does.
   */
  const editNoFollowupReason = useMemo(
    () =>
      (noFollowUpReason === NoFollowUpReasonEnum.OTHER
        ? noFollowUpOtherReason
        : noFollowUpReason) ?? '',
    [noFollowUpReason, noFollowUpOtherReason]
  )

  const editValues: AmendValues = useMemo(
    () => ({
      referToSpecialist: splitReferToSpecialist(referToSpecialist).fixed,
      labsOrdered: !!labsResponse,
      imagingOrdered: !!imagingResponse,
      // A picked date is a follow-up too; reading only `timeline` dropped it.
      followup: timeline || formatFollowupDate(timelineDate),
      noFollowupReason: editNoFollowupReason,
      text: noteForPatient,
      attachments: selectedNote?.attachments,
      removedAttachmentIds,
    }),
    [
      referToSpecialist,
      labsResponse,
      imagingResponse,
      timeline,
      timelineDate,
      editNoFollowupReason,
      noteForPatient,
      selectedNote?.attachments,
      removedAttachmentIds,
    ]
  )

  const editOps = useMemo(
    () => (editBaseline ? buildAddendumOps(editBaseline, editValues) : []),
    [editBaseline, editValues]
  )

  /** Seeds the wizard from the signed note so the diff has a like-for-like baseline. */
  const startEdit = useCallback(
    (note: AppointmentNote, appointment: Appointment) => {
      const baseline = noteToAmendValues(note)

      setSelectedNote(note)
      setSelectedAppointment(appointment)
      setEditBaseline(baseline)
      setLabsResponse(baseline.labsOrdered)
      setImagingResponse(baseline.imagingOrdered)
      setReferToSpecialist(note.referToSpecialist ?? undefined)
      // Orders reads these from context. Left unseeded, an edit opened straight
      // from the list showed "no Rx interest" on a note that records one.
      setRxInterest(note.rxInterest ?? undefined)
      setRxInterestMedications(note.rxInterestMedications ?? [])
      setTimeline(baseline.followup)
      setTimelineDate('')
      setNoteForPatient(baseline.text)
      // Never open on the "are you sure" confirmation; the withdrawal is
      // recorded on the follow-up step instead.
      setShowNoFollowUp(false)
      setNoFollowUpReason(note.noFollowupReason ?? undefined)
      setNoFollowUpOtherReason('')
      setSelectedFiles([])
      setRemovedAttachmentIds([])
      setEditSavedMessage('')
      // An inline note-for-patient edit left open would restore its pre-edit
      // text on cancel and hide whatever this edit just saved.
      setIsEditingNoteForPatient(false)
      setOriginalNoteForPatient(null)

      const steps = editScreens(appointment.type)
      setStepper(steps)
      setIsEditing(true)
      setCurrentScreen(steps[0]?.screen ?? 'amend-review')
    },
    []
  )

  /** Review falls back to context for anything the note lacks, so a cancelled
   *  edit has to put every field back, not just the text. */
  const cancelEdit = useCallback(() => {
    setSelectedFiles([])
    setRemovedAttachmentIds([])
    setIsEditing(false)
    setEditBaseline(undefined)
    setStepper([])
    setNoteForPatient(selectedNote?.text ?? '')
    setLabsResponse(selectedNote?.labsOrdered ?? undefined)
    setImagingResponse(selectedNote?.imagingOrdered ?? undefined)
    setReferToSpecialist(selectedNote?.referToSpecialist ?? undefined)
    setTimeline(selectedNote?.followup ?? '')
    setTimelineDate('')
    setNoFollowUpReason(selectedNote?.noFollowupReason ?? undefined)
    setNoFollowUpOtherReason('')
    setShowNoFollowUp(false)
    setCurrentScreen('review')
  }, [selectedNote])

  const saveEdit = useCallback(
    async (reason: string) => {
      if (
        !selectedNote ||
        (editOps.length === 0 &&
          selectedFiles.every((list) => list.length === 0))
      ) {
        return
      }

      setIsLoading(true)

      try {
        const response = await handleCreateAddendum({
          noteId: selectedNote.id,
          ops: editOps,
          files: selectedFiles,
          comment: reason.trim() || undefined,
        })

        // The server recomputes the note, so follow what it stored rather than
        // the form's own idea of the result.
        const saved = response?.data?.note

        if (saved) {
          // The signed files do not change with an edit, and the save response
          // does not carry them.
          setSelectedNote({
            ...saved,
            originalAttachments:
              saved.originalAttachments ?? selectedNote.originalAttachments,
          })
          setNoteForPatient(saved.text ?? '')
        }

        setSelectedFiles([])
        setRemovedAttachmentIds([])
        setIsEditing(false)
        setEditBaseline(undefined)
        setStepper([])
        setEditSavedMessage(NOTE_UPDATED_MESSAGE)
        setCurrentScreen('review')
      } catch {
        // createAddendum already alerted with the server's own message; a
        // second setAlertText here overwrote it with a generic axios string.
      } finally {
        setIsLoading(false)
      }
    },
    [selectedNote, editOps, selectedFiles, handleCreateAddendum, setAlertText]
  )

  const previousScreen = useCallback(() => {
    if (!selectedAppointment) {
      return
    }

    const context = {
      moreTestsOrdered: labsResponse || imagingResponse,
      noFollowUp: showNoFollowUp,
    }

    if (isEditing) {
      // Stepping back off the first edit step leaves the edit flow rather than
      // falling through to the wizard's 'initial', which would drop the note.
      const editScreen = getEditPreviousScreen(
        currentScreen,
        selectedAppointment.type
      )

      if (!editScreen) {
        cancelEdit()
        return
      }

      setCurrentScreen(editScreen)
      return
    }

    const screen = getPreviousScreen(
      currentScreen,
      selectedAppointment.type,
      context
    )

    if (screen === 'initial') {
      reset()
    }

    setCurrentScreen(screen)
  }, [
    currentScreen,
    selectedAppointment,
    labsResponse,
    imagingResponse,
    showNoFollowUp,
    isEditing,
    cancelEdit,
    setCurrentScreen,
  ])

  /**
   * Updates the "note for patient" text in the state.
   * This is just a state updater that handles the textarea onChange event.
   * The actual saving is handled by different functions depending on the context:
   * - For initial notes: confirmAndSend
   * - For signed notes: saveNoteForPatient
   */
  const updateNoteForPatient = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    setNoteForPatient(event.target.value)
  }

  /**
   * Initiates inline editing of the "note for patient" text field.
   * This is only available for signed appointment notes, as unsaved/draft appointment notes
   * should be edited through the regular note creation flow using the "back" navigation.
   *
   * Stores the current "note for patient" text in originalNoteForPatient to support cancellation.
   */
  const startEditingNoteForPatient = useCallback(() => {
    setOriginalNoteForPatient(noteForPatient) // Store current text as backup before editing
    setIsEditingNoteForPatient(true)
  }, [noteForPatient])

  /**
   * Cancels inline editing of the "note for patient" text field.
   * Restores the original text that was present before editing started.
   * Only applicable for signed appointment notes being edited inline.
   */
  const cancelEditingNoteForPatient = useCallback(() => {
    if (originalNoteForPatient !== null) {
      setNoteForPatient(originalNoteForPatient) // Restore text to pre-edit state
    }

    setIsEditingNoteForPatient(false)
    setOriginalNoteForPatient(null) // Clear backup after cancelling
  }, [originalNoteForPatient])

  /**
   * Saves changes to the "note for patient" text field.
   * This is only used for inline editing of signed appointment notes' "note for patient" field.
   * For unsaved/draft appointment notes, this field should be modified through the regular
   * note creation flow using the "back" navigation.
   *
   * If saving fails, the original text is restored and editing mode is cancelled.
   */
  const saveNoteForPatient = useCallback(async () => {
    try {
      setIsSavingNoteForPatient(true)

      // This should never happen as the save button is only shown for signed notes
      if (!selectedNote) {
        setAlertText(
          'Internal error: Attempted to save note for patient without a selected note'
        )
        return
      }

      const response = await handleSaveNoteForPatient({
        noteId: selectedNote.id,
        noteForPatient: noteForPatient,
      })

      if (response?.data) {
        updateSelectedNote({
          ...selectedNote,
          text: response.data.text,
        })
      }

      setIsEditingNoteForPatient(false)
      setOriginalNoteForPatient(null) // Clear backup after successful save
    } catch (error) {
      if (originalNoteForPatient !== null) {
        setNoteForPatient(originalNoteForPatient) // Restore text if save failed
      }

      setIsEditingNoteForPatient(false)
      setOriginalNoteForPatient(null) // Clear backup after restoring original
      setAlertText(
        `Unable to save "Note for patient".\n\n${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    } finally {
      setIsSavingNoteForPatient(false)
    }
  }, [
    selectedNote,
    noteForPatient,
    originalNoteForPatient,
    handleSaveNoteForPatient,
    updateSelectedNote,
    setAlertText,
  ])

  return (
    <PostAppointmentContext.Provider
      value={{
        currentScreen,
        noFollowUpReason,
        labsResponse,
        imagingResponse,
        referToSpecialist,
        rxInterest,
        rxInterestMedications,
        timeline,
        timelineDate,
        noteForPatient,
        recommendedProgram,
        stepper,
        selectedFiles,
        selectedNote,
        selectedAppointment,
        isLoading,
        showNoFollowUp,
        noFollowUpOtherReason,
        isEditingNoteForPatient,
        isSavingNoteForPatient,
        originalNoteForPatient,
        hasNoteForPatientChanged,
        setCurrentScreen,
        nextScreen,
        previousScreen,
        updateTestsResponse,
        updateNoReasonFollowUp,
        updateEditNoFollowup,
        updateTimeline,
        updateRecommendedProgram,
        handleAttachments,
        handleRemoveAttachment,
        updateSelectedNote,
        updateSelectedAppointment,
        confirmAndSend,
        updateShowFollowUp,
        updateNoFollowUpOtherReason,
        startEditingNoteForPatient,
        cancelEditingNoteForPatient,
        saveNoteForPatient,
        handleCreateAddendum,
        updateNoteForPatient,
        removedAttachmentIds,
        toggleAttachmentRemoval,
        isEditing,
        editOps,
        editValues,
        editBaseline,
        editSavedMessage,
        startEdit,
        cancelEdit,
        saveEdit,
        resetValues: reset,
      }}
    >
      {children}
    </PostAppointmentContext.Provider>
  )
}

export const usePostAppointmentContext = (): PostAppointmentInterface =>
  useContext(PostAppointmentContext)
